import React from "react";
import { motion } from "framer-motion";
import { Reveal } from "../components/Reveal";
import { Marquee } from "../components/Marquee";
import { useLang } from "../context/LanguageContext";
import { T } from "../i18n/translations";
import { MEDIA } from "../data/media";

const EASE = [0.16, 1, 0.3, 1];

const Img = ({ src, alt, className }) => (
  <div className={`relative overflow-hidden border border-trise-brass/40 ${className}`}>
    <motion.img
      src={src}
      alt={alt}
      loading="lazy"
      className="w-full h-full object-cover"
      initial={{ scale: 1.12 }}
      whileInView={{ scale: 1 }}
      viewport={{ once: true }}
      transition={{ duration: 1.4, ease: EASE }}
    />
  </div>
);

export default function Storia() {
  const { lang } = useLang();
  const s = T.storia_page;
  return (
    <>
      <section className="relative bg-trise-blue text-trise-ivory pt-36 pb-20 md:pt-44 md:pb-28 grain overflow-hidden" data-testid="storia-header">
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <span className="text-xs uppercase tracking-widen text-trise-brass">— {s.tag[lang]}</span>
          </Reveal>
          <Reveal delay={0.05}>
            <h1 className="mt-4 text-5xl md:text-7xl lg:text-8xl font-bold tracking-tight max-w-4xl leading-[0.95]">
              {s.heading1[lang]}
            </h1>
          </Reveal>
        </div>
      </section>

      {/* Origin story */}
      <section className="bg-trise-ivory py-24 md:py-32" data-testid="storia-origin">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-12 md:gap-16 items-start">
          <div className="md:col-span-7 order-2 md:order-1 flex flex-col gap-7">
            <Reveal>
              <p className="text-2xl md:text-3xl font-light leading-[1.4] text-trise-ink">{s.p1[lang]}</p>
            </Reveal>
            <Reveal delay={0.05}>
              <p className="text-base md:text-lg text-trise-ink/70 leading-relaxed">{s.p2[lang]}</p>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="text-base md:text-lg text-trise-ink/70 leading-relaxed">{s.p3[lang]}</p>
            </Reveal>
          </div>
          <div className="md:col-span-5 order-1 md:order-2 md:mt-8">
            <Reveal delay={0.1}>
              <Img src={MEDIA.tartare} alt="Piatto Trisé" className="aspect-[3/4]" />
            </Reveal>
          </div>
        </div>
      </section>

      <Marquee text={T.marquee[lang]} />

      {/* Room & veranda */}
      <section className="bg-trise-white py-24 md:py-32" data-testid="storia-veranda">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-12 md:gap-16 items-center">
          <div className="md:col-span-6">
            <Reveal>
              <Img src={MEDIA.pasta} alt="La veranda del bistrot" className="aspect-[4/3]" />
            </Reveal>
          </div>
          <div className="md:col-span-6">
            <Reveal delay={0.1}>
              <p className="mt-6 text-base md:text-lg text-trise-ink/70 leading-relaxed max-w-xl">{s.p4[lang]}</p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Location */}
      <section className="bg-trise-blue text-trise-ivory py-24 md:py-32 grain relative overflow-hidden" data-testid="storia-location">
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-12 md:gap-16 items-center">
          <div className="md:col-span-6">
            <Reveal>
              <h2 className="text-4xl md:text-5xl font-bold tracking-tight">{s.heading3[lang]}</h2>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="mt-6 text-base md:text-lg text-trise-ivory/75 leading-relaxed max-w-xl">{s.p5[lang]}</p>
            </Reveal>
          </div>
          <div className="md:col-span-6">
            <Reveal delay={0.05}>
              <Img src={MEDIA.dessert} alt="Atmosfera Trisé" className="aspect-[4/3]" />
            </Reveal>
          </div>
        </div>
      </section>

      {/* Closing */}
      <section className="bg-trise-ivory py-28 md:py-40" data-testid="storia-closing">
        <div className="max-w-5xl mx-auto px-6 md:px-12 text-center">
          <Reveal>
            <p className="text-3xl md:text-5xl lg:text-6xl font-bold tracking-tight text-trise-ink leading-[1.1]">
              {s.closing[lang]}
            </p>
          </Reveal>
        </div>
      </section>
    </>
  );
}
